// RoGroupedView — default body of the LocationDrillDown page in Phase 4 of
// the MEC UX overhaul. Groups every mec_match row by repair order and
// surfaces the status flags (matched / over-billed / under-billed /
// credit pending / leakage / unattributed) so a bookkeeper can scan from
// the RO level instead of pivoting through matcher buckets.
//
// Click an RO row to inline-expand a MatchTable scoped to that RO's
// contributing matches.
//
// Props:
//   matches            : array of mec_match rows (from listMatches)
//   onMatchResolved    : forwarded to MatchTable for Tier 2 confirm/reject
//   shopIdNum / periodStart / periodEnd / domain — used to name CSV exports

import { Fragment, useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, Download, Search, Loader2,
} from 'lucide-react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import { formatMoney, formatDate } from '../../../utils/formatters';
import { rowsToCsv, downloadCsv, centsToDollarsRaw } from '../csvHelpers';
import MatchTable from './MatchTable';
import PoAuditAccordion from './PoAuditAccordion';
import {
  computeRoFlags,
  RO_STATUS_LABELS,
  primaryRoStatus,
} from './roStatusFlags';

// Lump-method rows hide their per-vendor truth inside a group-level number.
// When an RO's children include these methods, the expanded view renders the
// per-vendor PoAuditAccordion (per PO#) instead of the flat MatchTable so the
// reviewer sees which specific vendor is over/under-billed — preserving the
// D-039 Option B audit semantics from the dedicated Ambiguous / Tier 1
// Mismatch tabs in Audit Mode.
const LUMP_METHODS_TO_AUDIT = new Set([
  'ambiguous',
  'tier_1_total_mismatch',
]);

const FILTER_CHIPS = [
  { key: 'all', label: 'All', tone: 'neutral' },
  { key: 'matched', label: 'Matched', tone: 'recovery' },
  { key: 'over_billed', label: 'Over-billed', tone: 'danger' },
  { key: 'under_billed', label: 'Under-billed', tone: 'action' },
  { key: 'credit_pending', label: 'Credit Pending', tone: 'insight' },
  { key: 'leakage', label: 'Leakage', tone: 'danger' },
  { key: 'unattributed', label: 'Unattributed', tone: 'neutral' },
];

const COLUMNS = [
  { key: 'ro_number',   label: 'RO #',         align: 'left',  sortable: true,  width: 140 },
  { key: 'status',      label: 'Status',       align: 'left',  sortable: false                 },
  { key: 'qbo_cents',   label: 'QBO $',        align: 'right', sortable: true,  width: 110 },
  { key: 'tek_cents',   label: 'Tek $',        align: 'right', sortable: true,  width: 110 },
  { key: 'delta',       label: 'Δ',            align: 'right', sortable: true,  width: 110 },
  { key: 'line_count',  label: '# Lines',      align: 'right', sortable: true,  width: 80  },
  { key: 'newest_date', label: 'Latest Date',  align: 'right', sortable: true,  width: 110 },
  { key: 'expander',    label: '',             align: 'right', sortable: false, width: 36  },
];

export default function RoGroupedView({
  matches,
  onMatchResolved,
  shopIdNum,
  periodStart,
  periodEnd,
  domain = 'parts',
  closeRunId = null,
  loading = false,
}) {
  const [filter, setFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState('qbo_cents');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedKey, setExpandedKey] = useState(null);

  const safeMatches = Array.isArray(matches) ? matches : [];

  const groups = useMemo(() => computeRoFlags(safeMatches), [safeMatches]);

  // Per-chip counts (always full-set, ignoring search).
  const counts = useMemo(() => {
    const c = {
      all: groups.length,
      matched: 0,
      over_billed: 0,
      under_billed: 0,
      credit_pending: 0,
      leakage: 0,
      unattributed: 0,
    };
    for (const g of groups) {
      if (g.key === '__unattributed__') c.unattributed++;
      for (const f of g.status_flags) {
        if (f in c) c[f]++;
      }
    }
    return c;
  }, [groups]);

  const filtered = useMemo(() => {
    let out = groups;
    if (filter === 'unattributed') {
      out = out.filter((g) => g.key === '__unattributed__');
    } else if (filter !== 'all') {
      out = out.filter((g) => g.status_flags.includes(filter));
    }
    if (searchText) {
      const needle = searchText.toLowerCase();
      out = out.filter((g) => {
        const ro = (g.ro_number || g.ro_id || '').toString().toLowerCase();
        if (ro.includes(needle)) return true;
        const ids = new Set(g.child_match_ids || []);
        for (const m of safeMatches) {
          if (!ids.has(m.id)) continue;
          if ((m.qbo_vendor || '').toLowerCase().includes(needle)) return true;
          if ((m.matched_po_number || '').toLowerCase().includes(needle)) return true;
          if ((m.qbo_memo || '').toLowerCase().includes(needle)) return true;
        }
        return false;
      });
    }
    return out;
  }, [groups, filter, searchText, safeMatches]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const aVal = sortValue(a, sortKey);
      const bVal = sortValue(b, sortKey);
      let cmp;
      if (aVal == null && bVal == null) cmp = 0;
      else if (aVal == null) cmp = 1;
      else if (bVal == null) cmp = -1;
      else if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
      else cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const onSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('desc');
    }
  };

  const exportCsv = () => {
    const csv = rowsToCsv(sorted, [
      { key: 'ro_number', label: 'RO #', accessor: (r) => r.ro_number || r.ro_id || '— unattributed —' },
      { key: 'status', label: 'Status', accessor: (r) => r.status_flags.join('; ') || 'none' },
      { key: 'qbo', label: 'QBO Total', accessor: (r) => centsToDollarsRaw(r.qbo_cents) },
      { key: 'tek', label: 'Tek Total', accessor: (r) => centsToDollarsRaw(r.tek_cents) },
      { key: 'delta', label: 'Δ', accessor: (r) => centsToDollarsRaw((r.qbo_cents || 0) - (r.tek_cents || 0)) },
      { key: 'line_count', label: '# Lines' },
      { key: 'newest_date', label: 'Latest Date' },
    ]);
    downloadCsv(
      `mec-per-ro-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`,
      csv,
    );
  };

  const totals = useMemo(() => {
    let q = 0;
    let t = 0;
    for (const g of filtered) {
      q += g.qbo_cents || 0;
      t += g.tek_cents || 0;
    }
    return { q, t };
  }, [filtered]);

  return (
    <Card padded={false}>
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold text-[var(--color-warm-900)] leading-tight">
            Repair orders in this period
          </h3>
          <p className="text-[12px] text-[var(--color-warm-500)] mt-0.5">
            Every QBO line grouped by its repair order. Click a row to see the underlying matches.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-warm-400)] pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search RO, vendor, PO, memo…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="h-8 pl-7 pr-3 text-[12px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] focus:outline-none focus:border-[var(--color-warm-400)] w-64"
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download size={12} />}
            onClick={exportCsv}
            disabled={sorted.length === 0}
          >
            CSV
          </Button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="px-5 pb-3 flex items-center gap-1.5 flex-wrap">
        {FILTER_CHIPS.map((chip) => {
          const active = filter === chip.key;
          const count = counts[chip.key] || 0;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setFilter(chip.key)}
              className={`
                inline-flex items-center gap-1.5 px-2.5 h-7 text-[11.5px] font-semibold rounded-[var(--radius-full)] border transition-colors
                ${active
                  ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] border-[var(--color-warm-900)]'
                  : 'bg-[var(--color-cream-50)] text-[var(--color-warm-600)] border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)]'}
              `}
            >
              {chip.label}
              {count > 0 && (
                <Badge tone={active ? 'cream' : chip.tone} size="xs">
                  {count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      <div className="px-5 pb-2 text-[11px] text-[var(--color-warm-500)] flex items-center gap-2 flex-wrap">
        {sorted.length} RO{sorted.length === 1 ? '' : 's'}
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="tabular-nums font-semibold text-[var(--color-warm-700)]">
          QBO {formatMoney((totals.q || 0) / 100)} · Tek {formatMoney((totals.t || 0) / 100)}
        </span>
        {searchText && sorted.length !== groups.length && (
          <>
            <span className="text-[var(--color-warm-300)]">·</span>
            <span>filtered from {groups.length}</span>
          </>
        )}
      </div>

      <div className="overflow-x-auto border-t border-[var(--color-warm-100)]">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--color-cream-100)] border-b border-[var(--color-warm-100)]">
              {COLUMNS.map((c) => (
                <Th
                  key={c.key}
                  align={c.align}
                  width={c.width}
                  sortable={c.sortable}
                  active={sortKey === c.key}
                  direction={sortKey === c.key ? sortDir : null}
                  onClick={() => onSort(c)}
                >
                  {c.label}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-5 py-10 text-center">
                  <div className="inline-flex items-center gap-2 text-[12px] text-[var(--color-warm-500)]">
                    <Loader2 size={13} className="animate-spin" />
                    Loading repair orders…
                  </div>
                </td>
              </tr>
            )}
            {!loading && groups.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-5 py-12 text-center text-[12px] text-[var(--color-warm-500)]">
                  No matches yet for this period. Run a close to populate this view.
                </td>
              </tr>
            )}
            {!loading && groups.length > 0 && sorted.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-5 py-12 text-center text-[12px] text-[var(--color-warm-500)]">
                  No matches in the selected filter.
                </td>
              </tr>
            )}
            {!loading && sorted.map((g) => {
              const expanded = expandedKey === g.key;
              const delta = (g.qbo_cents || 0) - (g.tek_cents || 0);
              const primary = primaryRoStatus(g.status_flags || []);
              const isUnattributed = g.key === '__unattributed__';
              const childIds = new Set(g.child_match_ids || []);
              const childRows = safeMatches.filter((m) => childIds.has(m.id));

              return (
                <Fragment key={g.key}>
                  <tr
                    onClick={() => setExpandedKey(expanded ? null : g.key)}
                    className={`
                      border-b border-[var(--color-warm-100)] cursor-pointer
                      ${expanded ? 'bg-[var(--color-cream-100)]/70' : 'hover:bg-[var(--color-cream-100)]/40'}
                      transition-colors
                    `}
                  >
                    <td className="px-3 py-3 align-middle">
                      {isUnattributed ? (
                        <span className="text-[12px] italic text-[var(--color-warm-500)]">— unattributed —</span>
                      ) : (
                        <span className="text-[13px] font-semibold mono-nums text-[var(--color-warm-900)]">
                          RO #{g.ro_number || g.ro_id}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <RoStatusPills flags={g.status_flags || []} primary={primary} />
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
                      {formatMoney((g.qbo_cents || 0) / 100)}
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums text-[var(--color-warm-700)]">
                      {formatMoney((g.tek_cents || 0) / 100)}
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums">
                      <span className={`text-[12px] font-semibold ${
                        delta === 0
                          ? 'text-[var(--color-warm-500)]'
                          : delta > 0
                            ? 'text-[var(--color-danger-700)]'
                            : 'text-[var(--color-recovery-700)]'
                      }`}>
                        {delta > 0 ? '+' : ''}{formatMoney(delta / 100)}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums text-[var(--color-warm-700)]">
                      {g.line_count || 0}
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-[var(--color-warm-600)]">
                      {g.newest_date ? formatDate(g.newest_date) : '—'}
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-[var(--color-warm-500)]">
                      {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-[var(--color-cream-100)]/40">
                      <td colSpan={COLUMNS.length} className="px-3 py-4">
                        <div className="px-2 space-y-3">
                          {(() => {
                            // Find lump children (ambiguous / tier_1_total_mismatch)
                            // and group them by matched_po_number. Each unique
                            // PO# gets its own PoAuditAccordion so the reviewer
                            // sees per-vendor over/under-billing instead of a
                            // flat list of identical-looking rows.
                            const lumpPoNumbers = new Set();
                            const nonLumpRows = [];
                            for (const row of childRows) {
                              const isLump = LUMP_METHODS_TO_AUDIT.has(row.matched_method);
                              if (isLump && row.matched_po_number && closeRunId) {
                                lumpPoNumbers.add(String(row.matched_po_number));
                              } else {
                                nonLumpRows.push(row);
                              }
                            }
                            const lumpList = Array.from(lumpPoNumbers).sort();
                            return (
                              <>
                                {lumpList.map((poNum) => (
                                  <div
                                    key={`audit-${poNum}`}
                                    className="rounded-[var(--radius-md)] border border-[var(--color-warm-200)] bg-[var(--color-cream-50)] overflow-hidden"
                                  >
                                    <div className="px-4 py-2 bg-[var(--color-warm-50)] border-b border-[var(--color-warm-200)] text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-600)]">
                                      Per-vendor audit — PO {poNum}
                                    </div>
                                    <PoAuditAccordion
                                      closeRunId={closeRunId}
                                      poNumber={poNum}
                                      domain={domain}
                                    />
                                  </div>
                                ))}
                                {nonLumpRows.length > 0 && (
                                  <MatchTable
                                    rows={nonLumpRows}
                                    title={
                                      isUnattributed
                                        ? 'Unattributed matches'
                                        : lumpList.length > 0
                                          ? `Other contributing lines — RO #${g.ro_number || g.ro_id}`
                                          : `Contributing matches — RO #${g.ro_number || g.ro_id}`
                                    }
                                    description="Underlying QBO ↔ Tekmetric lines for this group."
                                    emptyMessage="No contributing matches."
                                    csvFilename={`mec-ro-${g.ro_number || g.ro_id || 'unattributed'}-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                                    onMatchResolved={onMatchResolved}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RoStatusPills({ flags, primary }) {
  if (!flags || flags.length === 0) {
    return (
      <span className="text-[11px] italic text-[var(--color-warm-400)]">none</span>
    );
  }
  // Primary flag first, others after.
  const ordered = [primary, ...flags.filter((f) => f !== primary)];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {ordered.map((f) => {
        const meta = RO_STATUS_LABELS[f];
        if (!meta) return null;
        return (
          <Badge key={f} tone={meta.tone} size="xs">
            {meta.label}
          </Badge>
        );
      })}
    </div>
  );
}

function sortValue(row, key) {
  switch (key) {
    case 'ro_number':
      return row.ro_number || row.ro_id || '';
    case 'qbo_cents':
      return row.qbo_cents || 0;
    case 'tek_cents':
      return row.tek_cents || 0;
    case 'delta':
      return (row.qbo_cents || 0) - (row.tek_cents || 0);
    case 'line_count':
      return row.line_count || 0;
    case 'newest_date':
      return row.newest_date || '';
    default:
      return null;
  }
}

function Th({ children, align = 'left', width, sortable, active, direction, onClick }) {
  return (
    <th
      style={width ? { width: `${width}px` } : undefined}
      onClick={onClick}
      className={`
        px-3 py-2.5 text-[10px] font-bold uppercase tracking-[1px]
        ${active ? 'text-[var(--color-warm-900)]' : 'text-[var(--color-warm-500)]'}
        ${align === 'right' ? 'text-right' : 'text-left'}
        ${sortable ? 'cursor-pointer select-none hover:text-[var(--color-warm-800)]' : ''}
        whitespace-nowrap
      `}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && active && (
          <span className="text-[10px]">{direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );
}
